/**
 * YAML → CSV Tool
 *
 * Converts YAML to CSV by parsing to a JS object and serializing with PapaParse.
 * Expects YAML representing an array of objects.
 */

import * as Papa from 'papaparse';
import { parse as parseYaml } from 'yaml';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const YamlToCsvTool: ITransformerTool = {
  id: 'yaml-to-csv',
  name: 'YAML → CSV',
  description: 'Convert YAML to CSV',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['yaml', 'csv', 'convert', 'transform', 'tabular'],

  seo: {
    slug: 'yaml-to-csv',
    pageTitle: 'YAML to CSV Converter - Free Online Tool | Prism',
    metaDescription: 'Convert YAML arrays to CSV format instantly. Handles nested objects and consistent column ordering. Fast, private, runs entirely in your browser.',
    keywords: ['yaml to csv', 'yaml to csv converter', 'convert yaml to csv online', 'yaml csv transformer'],
    relatedTools: ['csv-to-yaml', 'yaml-to-json', 'json-to-csv'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.YAML],
  outputType: SEMANTIC_TYPES.CSV,

  attributes: [
    {
      name: 'includeHeader',
      type: 'boolean',
      label: 'Include Header',
      defaultValue: true,
      description: 'Include a header row with column names',
    },
    {
      name: 'delimiter',
      type: 'select',
      label: 'Delimiter',
      defaultValue: ',',
      options: [',', ';'],
      description: 'Column separator character',
    },
  ],

  supportsInput(_input: string, detectedType?: string): boolean {
    return detectedType === 'YAML';
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const includeHeader = config.includeHeader !== false;
    const delimiter = config.delimiter === ';' ? ';' : ',';

    let parsed: unknown;
    try {
      parsed = parseYaml(input);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Invalid YAML';
      throw ToolError.validation(`Invalid YAML: ${msg}`, {
        suggestion: 'Check indentation and ensure mappings use "key: value" syntax',
      });
    }

    // Normalize to array
    const items: unknown[] = Array.isArray(parsed)
      ? parsed
      : parsed !== null && typeof parsed === 'object'
        ? [parsed]
        : [];

    if (items.length === 0) {
      throw ToolError.validation('YAML must contain an array of objects or a single object', {
        suggestion: 'Provide a YAML array (lines starting with -) or a mapping',
      });
    }

    // Flatten nested objects one level for CSV compatibility
    const flatItems = items.map((item) => {
      if (item === null || typeof item !== 'object') return { value: item };
      const flat: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(item as Record<string, unknown>)) {
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
          for (const [nk, nv] of Object.entries(value as Record<string, unknown>)) {
            flat[`${key}.${nk}`] = nv;
          }
        } else {
          flat[key] = Array.isArray(value) ? JSON.stringify(value) : value;
        }
      }
      return flat;
    });

    // Collect all keys to ensure consistent columns
    const allKeys = Array.from(
      flatItems.reduce((set, obj) => {
        for (const k of Object.keys(obj)) set.add(k);
        return set;
      }, new Set<string>())
    );

    const data: Array<Array<string | number | boolean | null>> = [];
    if (includeHeader) {
      data.push(allKeys);
    }
    for (const obj of flatItems) {
      data.push(
        allKeys.map((k) => {
          const v = (obj as Record<string, unknown>)[k];
          if (v === null || v === undefined) return null;
          if (typeof v === 'object') return JSON.stringify(v);
          return v as string | number | boolean;
        })
      );
    }

    return Papa.unparse(data, { delimiter, quotes: true });
  },
};
